// HOOKS
import { useMutation } from "@apollo/client";
import { useState, useCallback, useEffect } from "react";
import useAbortController from "../other/useAbortController.other.hook"; // Import the custom hook

// GRAPHQL
import { UPDATE_SHIPMENT } from "../../graphql/mutations/shipments/shipments.mutations";

// recursively remove __typename from nested objects
const removeTypename = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((value) => removeTypename(value));
  } else if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((newObj, key) => {
      if (key !== "__typename") {
        newObj[key] = removeTypename(obj[key]);
      }
      return newObj;
    }, {});
  }
  return obj;
};

const useUpdateShipment = () => {
  const [timedUpdate, setTimedUpdate] = useState(null);
  const [updateShipmentMutation, { loading, error, data }] =
    useMutation(UPDATE_SHIPMENT);

  const { createAbortController, signal } = useAbortController(); // Use abort controller hook

  // Using useCallback to ensure function stability if used as a dependency elsewhere
  const updateShipment = useCallback(
    async (shipment) => {
      if (timedUpdate) clearTimeout(timedUpdate);

      const controller = createAbortController(); // Create or reuse an AbortController for this request

      const timeout = setTimeout(() => {
        // Remove __typename from input
        const filteredShipment = removeTypename(shipment);

        updateShipmentMutation({
          variables: { shipment: { ...filteredShipment } },
          context: {
            fetchOptions: {
              signal: controller.signal, // Pass the signal for cancellation
            },
          },
        }).catch((apolloError) => {
          if (apolloError.name === "AbortError") {
            console.log("Request canceled:", apolloError.message);
          } else {
            console.error(
              `Apollo error updating shipment: ${apolloError.message}`
            );
          }
        });
      }, 800);

      setTimedUpdate(timeout);
    },
    [updateShipmentMutation, timedUpdate, createAbortController]
  );

  // Ensure to clear the timeout and abort the ongoing request on component unmount
  useEffect(() => {
    return () => {
      if (timedUpdate) clearTimeout(timedUpdate);
    };
  }, [timedUpdate]);

  return { updateShipment, loading, error, data };
};

export default useUpdateShipment;
